import {
  ComponentConfig,
  ComponentType,
  LayoutTypes,
  ValidationRule,
} from "../../services/types/ComponentTypes";
import { matchesFieldVisibilityRule } from "./configUtils";

function isConditionalRequiredRule(rule: { rule?: string }): boolean {
  const r = String(rule?.rule ?? "")
    .toLowerCase()
    .replace(/_/g, "");
  return r === "conditionalrequired";
}

function lookupOptionsForField(
  lookups: Record<string, Array<{ value?: unknown; id?: unknown; label?: string; name?: string }>> | undefined,
  lookupName: string | undefined
): Array<{ value?: unknown; id?: unknown; label?: string; name?: string }> {
  if (!lookups || !lookupName) return [];
  if (Array.isArray(lookups[lookupName]) && lookups[lookupName].length > 0) {
    return lookups[lookupName];
  }
  const prefix = `${lookupName}_`;
  for (const [key, opts] of Object.entries(lookups)) {
    if (key === lookupName || key.startsWith(prefix)) {
      if (Array.isArray(opts) && opts.length > 0) return opts;
    }
  }
  return [];
}

const FORM_INPUT_TYPES = new Set<string>([
  ComponentType.TEXT_FIELD,
  ComponentType.SELECT_DROPDOWN,
  ComponentType.SEARCHABLE_DROPDOWN,
  ComponentType.DATE_FIELD,
  ComponentType.DATETIME_FIELD,
  ComponentType.TEXT_AREA,
  ComponentType.RADIO_FIELD,
  ComponentType.CHECKBOX,
  "money",
  "files",
]);

/**
 * Resolve a single [ref] (e.g. [qty], [issued_qty]) to a value from row data.
 * Ref is matched against control's Json_Attribute, name, or BindValue.
 */
function resolveRefInRow(
  ref: string,
  row: Record<string, any>,
  childControls: ComponentConfig[] | undefined
): string | null {
  if (!childControls?.length) return null;
  const cleanRef = ref.replace(/[\[\]]/g, "").trim();
  for (const c of childControls) {
    const anyC = c as any;
    const jsonAttr = anyC.Json_Attribute;
    const name = anyC.name ?? anyC.BindValue;
    if (jsonAttr === cleanRef || name === cleanRef) {
      const val = row[name] ?? row[jsonAttr] ?? row[cleanRef];
      if (val !== undefined && val !== null) return String(val);
      return null;
    }
  }
  // Fallback: use ref as key
  const val = row[cleanRef];
  if (val !== undefined && val !== null) return String(val);
  return null;
}

function isStrictNumericString(value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== "" && /^-?\d+(\.\d+)?$/.test(trimmed);
}

function quoteForConditionExpression(value: string): string {
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

/** Resolve a row/form operand for safe condition evaluation (avoids parseFloat("1abc") === 1). */
export function resolveConditionOperand(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "0";
  const asString = String(value);
  if (isStrictNumericString(asString)) return String(parseFloat(asString));
  return quoteForConditionExpression(asString);
}

/**
 * Evaluate a condition string against row data.
 * Condition uses [field] refs that map to Json_Attribute or name (e.g. [qty] >= [issued_qty]).
 * Returns true if the condition holds, false otherwise.
 * Safe: only replaces refs with values and evaluates comparison expression.
 */
export function evaluateCondition(
  condition: string,
  row: Record<string, any>,
  childControls: ComponentConfig[] | undefined
): boolean {
  if (!condition || typeof condition !== "string") return true;
  const refRegex = /\[[\w]+\]/g;
  let expr = condition;
  const matches = [...new Set(condition.match(refRegex) || [])];
  for (const m of matches) {
    const resolved = resolveRefInRow(m, row, childControls);
    const replacement = resolveConditionOperand(resolved);
    expr = expr.split(m).join(replacement);
  }
  // Normalize operators (allow spaces)
  expr = expr.replace(/\s+/g, " ").trim();
  // Only allow safe characters; strip quoted string literals before checking
  const exprForSafety = expr.replace(/"[^"\\]*(?:\\.[^"\\]*)*"/g, '""');
  if (!/^[\d.\s+\-*/()<=>!="]+$/.test(exprForSafety)) return true;
  try {
    const result = new Function(`return (${expr})`)();
    return Boolean(result);
  } catch {
    return true;
  }
}

export interface ConditionalValidationResult {
  message: string;
  isWarning: boolean;
  rowIndex: number;
  fieldLabel?: string;
}

/**
 * Run all conditional validations (ValidationConfig with rule CONDITION or condition prop)
 * on each row of submodule data. Returns list of failed validations (condition evaluated to false).
 */
export function runConditionalValidations(
  data: Record<string, any>[],
  childControls: ComponentConfig[] | undefined
): ConditionalValidationResult[] {
  const results: ConditionalValidationResult[] = [];
  if (!data?.length || !childControls?.length) return results;

  for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
    const row = data[rowIndex];
    for (const control of childControls) {
      const anyC = control as any;
      const validation = anyC.validation;
      const conditionConfig = anyC.conditionalValidation;
      const label = anyC.label ?? anyC.Label ?? anyC.name ?? anyC.BindValue ?? "Field";

      if (conditionConfig?.condition) {
        const ok = evaluateCondition(conditionConfig.condition, row, childControls);
        if (!ok && conditionConfig.message) {
          results.push({
            message: conditionConfig.message,
            isWarning: Boolean(conditionConfig.isWarning),
            rowIndex: rowIndex + 1,
            fieldLabel: label,
          });
        }
      }
      if (validation && Array.isArray(validation)) {
        for (const rule of validation) {
          const ctrlName = anyC.name ?? anyC.BindValue;
          const jsonAttr = anyC.Json_Attribute;
          const cellValue =
            (ctrlName && row[ctrlName] !== undefined && row[ctrlName] !== null)
              ? row[ctrlName]
              : (jsonAttr && row[jsonAttr] !== undefined && row[jsonAttr] !== null)
                ? row[jsonAttr]
                : null;

          if (rule.rule === ValidationRule.PATTERN && rule.value) {
            const cellValueStr = cellValue !== null ? String(cellValue) : "";
            if (cellValueStr.trim() !== "") {
              const regex = new RegExp(String(rule.value));
              if (!regex.test(cellValueStr)) {
                results.push({
                  message: rule.message || `${label} has an invalid format`,
                  isWarning: Boolean(rule.isWarning),
                  rowIndex: rowIndex + 1,
                  fieldLabel: label,
                });
              }
            }
          }

          if (rule.rule === ValidationRule.CONDITION && rule.condition) {
            const ok = evaluateCondition(rule.condition, row, childControls);
            if (!ok && rule.message) {
              results.push({
                message: rule.message,
                isWarning: Boolean(rule.isWarning),
                rowIndex: rowIndex + 1,
                fieldLabel: label,
              });
            }
          }

          // Conditional required: field is mandatory only when a sibling field matches a label/value
          if (isConditionalRequiredRule(rule) && rule.whenField) {
            const isRequired = isConditionallyRequired(rule, row, childControls);
            if (isRequired) {
              if (cellValue === null || cellValue === undefined || String(cellValue).trim() === "") {
                results.push({
                  message: rule.message || `${label} is required`,
                  isWarning: Boolean(rule.isWarning),
                  rowIndex: rowIndex + 1,
                  fieldLabel: label,
                });
              }
            }
          }
        }
      }
    }
  }
  return results;
}

function collectSiblingDataKeys(siblingCtrl: any, whenField: string): string[] {
  const keys = [
    siblingCtrl?.name,
    siblingCtrl?.BindValue,
    siblingCtrl?.Json_Attribute,
    whenField,
  ].filter((k): k is string => Boolean(k));
  return [...new Set(keys)];
}

function collectSiblingValues(
  row: Record<string, any>,
  keys: string[]
): unknown[] {
  return keys
    .map((k) => row[k])
    .filter((v) => v !== undefined && v !== null);
}

function isBooleanLikeControl(siblingCtrl: any): boolean {
  if (!siblingCtrl) return false;
  const type = String(
    siblingCtrl.componentType ?? siblingCtrl.ControlType ?? ""
  ).toLowerCase();
  return (
    type === ComponentType.RADIO_FIELD ||
    type === "radiofield" ||
    type === ComponentType.CHECKBOX ||
    type === "checkbox" ||
    siblingCtrl.ControlType === "CheckBox" ||
    Array.isArray(siblingCtrl.options)
  );
}

function isBooleanTruthy(value: unknown): boolean {
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (
    normalized === "false" ||
    normalized === "no" ||
    normalized === "n" ||
    normalized === "f"
  ) {
    return false;
  }
  return normalized === "true" || normalized === "yes" || normalized === "y";
}

function matchesBooleanWhenValue(
  whenValue: string,
  siblingValues: unknown[],
  isBooleanSibling: boolean
): boolean {
  const target = whenValue.toLowerCase();
  if (!isBooleanSibling) {
    return siblingValues.some((v) => String(v).toLowerCase() === target);
  }
  const hasTruthy = siblingValues.some((v) => isBooleanTruthy(v));
  const hasFalsy = siblingValues.some((v) => !isBooleanTruthy(v));
  if (target === "true") return hasTruthy;
  if (target === "false") {
    if (hasTruthy) return false;
    return hasFalsy || siblingValues.length === 0;
  }
  return siblingValues.some((v) => String(v).toLowerCase() === target);
}

/** Field names whose conditionalRequired rules depend on the changed sibling key(s). */
export function collectConditionalDependentFieldNames(
  changedKeys: string[],
  allFields: ComponentConfig[]
): string[] {
  const changed = new Set(changedKeys.filter(Boolean));
  const dependents: string[] = [];
  for (const field of allFields) {
    const anyField = field as Record<string, unknown>;
    const validation = anyField.validation;
    if (!Array.isArray(validation)) continue;
    for (const rule of validation) {
      if (!isConditionalRequiredRule(rule as { rule?: string })) continue;
      const whenField = (rule as { whenField?: string }).whenField;
      if (!whenField) continue;
      const siblingCtrl = allFields.find((c) => {
        const anyC = c as Record<string, unknown>;
        const n = (anyC.name as string | undefined) ?? (anyC.BindValue as string | undefined);
        const ja = anyC.Json_Attribute as string | undefined;
        const bv = anyC.BindValue as string | undefined;
        return (
          n === whenField ||
          ja === whenField ||
          bv === whenField ||
          anyC.name === whenField
        );
      }) as Record<string, unknown> | undefined;
      const siblingKeys = collectSiblingDataKeys(siblingCtrl, whenField);
      if (siblingKeys.some((k) => changed.has(k))) {
        const depName =
          (anyField.name as string | undefined) ??
          (anyField.BindValue as string | undefined);
        if (depName) dependents.push(depName);
        break;
      }
    }
  }
  return dependents;
}

/** True when any sibling alias has a non-empty (trimmed) value. */
function isSiblingNotEmpty(
  siblingRawValue: unknown,
  siblingValues: unknown[]
): boolean {
  const candidates =
    siblingValues.length > 0 ? siblingValues : [siblingRawValue];
  return candidates.some((v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === "string") return v.trim() !== "";
    if (typeof v === "number") return !Number.isNaN(v);
    if (typeof v === "boolean") return true;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v as object).length > 0;
    return String(v).trim() !== "";
  });
}

/**
 * Determine whether a conditionalRequired rule is active for a given row.
 * Checks the sibling field's selected-option label or raw value against whenLabel / whenValue,
 * or whether the sibling is non-empty when whenNotEmpty is set.
 */
export function isConditionallyRequired(
  rule: {
    whenField?: string;
    whenLabel?: string;
    whenValue?: string;
    whenNotEmpty?: boolean;
  },
  row: Record<string, any>,
  childControls: ComponentConfig[] | undefined,
  lookups?: Record<
    string,
    Array<{ value?: unknown; id?: unknown; label?: string; name?: string }>
  >
): boolean {
  if (!rule.whenField) return false;

  // Find the sibling control config
  const siblingCtrl = childControls?.find((c: any) => {
    const n = (c as any).name ?? (c as any).BindValue;
    const ja = (c as any).Json_Attribute;
    const bv = (c as any).BindValue;
    return (
      n === rule.whenField ||
      ja === rule.whenField ||
      bv === rule.whenField ||
      (c as any).name === rule.whenField
    );
  }) as any;

  const siblingFieldName = siblingCtrl?.name ?? siblingCtrl?.BindValue ?? rule.whenField;
  const siblingJsonAttr = siblingCtrl?.Json_Attribute;
  const siblingLookupName = siblingCtrl?.lookupName || siblingCtrl?.LookupName;
  const siblingDataKeys = collectSiblingDataKeys(siblingCtrl, rule.whenField);
  const siblingValues = collectSiblingValues(row, siblingDataKeys);
  const booleanSibling = isBooleanLikeControl(siblingCtrl);

  // Get the raw value of the sibling field (prefer any truthy boolean across aliases)
  let siblingRawValue =
    row[siblingFieldName] ??
    (siblingJsonAttr ? row[siblingJsonAttr] : undefined) ??
    row[rule.whenField];
  if (booleanSibling && siblingValues.some((v) => isBooleanTruthy(v))) {
    siblingRawValue = true;
  } else if (booleanSibling && siblingValues.length > 0) {
    siblingRawValue = siblingValues.find((v) => !isBooleanTruthy(v)) ?? siblingValues[0];
  }

  // 1) Match by label — same rules as fieldVisibilityRules (selectedData + raw value)
  if (rule.whenLabel) {
    if (booleanSibling) {
      const label = rule.whenLabel.toLowerCase();
      if (label === "no" || label === "false") {
        return matchesBooleanWhenValue("false", siblingValues, true);
      }
      if (label === "yes" || label === "true") {
        return matchesBooleanWhenValue("true", siblingValues, true);
      }
    }

    if (matchesFieldVisibilityRule(rule.whenField, rule.whenLabel, row, lookups)) {
      return true;
    }

    const target = rule.whenLabel.toLowerCase();

    // _selectedOption (some grid/submodule paths)
    const selectedOptionKey = `_${siblingFieldName}_selectedOption`;
    const selectedOption = row[selectedOptionKey];
    if (selectedOption) {
      const optLabel = String(
        selectedOption.label ??
          selectedOption.name ??
          selectedOption.description ??
          selectedOption.value ??
          ""
      ).toLowerCase();
      if (optLabel.includes(target)) return true;
    }

    // Radio / checkbox options on the sibling control config
    if (
      Array.isArray(siblingCtrl?.options) &&
      siblingRawValue !== undefined &&
      siblingRawValue !== null
    ) {
      const matchedOpt = siblingCtrl.options.find((opt: { value?: unknown }) => {
        const optVal = opt?.value;
        return (
          optVal === siblingRawValue ||
          String(optVal).toLowerCase() === String(siblingRawValue).toLowerCase()
        );
      });
      if (matchedOpt) {
        const optLabel = String(
          (matchedOpt as { label?: string; name?: string }).label ??
            (matchedOpt as { label?: string; name?: string }).name ??
            ""
        ).toLowerCase();
        if (optLabel.includes(target) || target.includes(optLabel)) return true;
      }
    }

    // Resolve label from cached lookup when value is an id (common for bill mode)
    if (siblingRawValue !== undefined && siblingRawValue !== null && lookups) {
      const options = lookupOptionsForField(lookups, siblingLookupName);
      const match = options.find(
        (opt) =>
          String(opt.value ?? "") === String(siblingRawValue) ||
          String(opt.id ?? "") === String(siblingRawValue)
      );
      if (match) {
        const optLabel = String(match.label ?? match.name ?? "").toLowerCase();
        if (optLabel.includes(target)) return true;
      }
    }
  }

  // 2) Match by value — direct equality (boolean radios/checkboxes use all alias keys)
  if (rule.whenValue) {
    if (matchesBooleanWhenValue(rule.whenValue, siblingValues, booleanSibling)) {
      return true;
    }
  }

  // 3) Match when sibling has any non-empty value (e.g. TRN filled → require VAT certificate)
  if (rule.whenNotEmpty === true) {
    return isSiblingNotEmpty(siblingRawValue, siblingValues);
  }

  return false;
}

/** Collect input field configs from a screen tree (for form-level conditional required). */
export function collectFormInputFields(
  components: ComponentConfig[] | undefined
): ComponentConfig[] {
  const fields: ComponentConfig[] = [];
  const walk = (items: ComponentConfig[] | undefined) => {
    if (!items?.length) return;
    for (const comp of items) {
      const anyComp = comp as Record<string, unknown>;
      const type = String(comp.componentType ?? "");
      if (FORM_INPUT_TYPES.has(type)) {
        fields.push(comp);
      }
      const nestedFields = anyComp.Fields as ComponentConfig[] | undefined;
      if (Array.isArray(nestedFields)) walk(nestedFields);
      if (Array.isArray(comp.childComponents)) walk(comp.childComponents);
      const childControls = anyComp.childControls as ComponentConfig[] | undefined;
      if (Array.isArray(childControls)) walk(childControls);
      if (
        comp.componentType === LayoutTypes.FIELD_GROUP &&
        Array.isArray(anyComp.childComponents as ComponentConfig[] | undefined)
      ) {
        walk(anyComp.childComponents as ComponentConfig[]);
      }
    }
  };
  walk(components);
  return fields;
}

function parseNumericBoundFromCondition(
  condition: string,
  operator: ">=" | "<="
): number | undefined {
  const pattern =
    operator === ">="
      ? />=\s*(-?\d+(?:\.\d+)?)\s*$/
      : /<=\s*(-?\d+(?:\.\d+)?)\s*$/;
  const match = String(condition).trim().match(pattern);
  if (!match) return undefined;
  const n = parseFloat(match[1]);
  return Number.isFinite(n) ? n : undefined;
}

/** When true, number inputs accept a leading minus (e.g. round off: +5 reduces total, -5 increases). */
export function allowsNegativeNumericInput(
  control: Record<string, unknown> | undefined
): boolean {
  if (!control) return false;
  return (
    control.AllowNegative === true ||
    control.allowNegative === true
  );
}

/** Resolve HTML min for number inputs from field config / validation rules. */
export function resolveNumericInputMin(control: Record<string, unknown> | undefined): number | undefined {
  if (!control) return undefined;
  for (const key of ["Min_Value", "minValue", "min"]) {
    const raw = control[key];
    if (raw !== undefined && raw !== null && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n)) return n;
    }
  }
  const validation = control.validation;
  if (!Array.isArray(validation)) return undefined;
  for (const rule of validation) {
    const ruleName = String(rule?.rule ?? "").toLowerCase().replace(/_/g, "");
    if (ruleName === "minvalue" && rule.value !== undefined && rule.value !== null && rule.value !== "") {
      const n = Number(rule.value);
      if (Number.isFinite(n)) return n;
    }
    if (ruleName === "condition" && rule.condition) {
      const bound = parseNumericBoundFromCondition(String(rule.condition), ">=");
      if (bound !== undefined) return bound;
    }
  }
  return undefined;
}

/** Resolve HTML max for number inputs from field config / validation rules. */
export function resolveNumericInputMax(control: Record<string, unknown> | undefined): number | undefined {
  if (!control) return undefined;
  for (const key of ["Max_Value", "maxValue", "max"]) {
    const raw = control[key];
    if (raw !== undefined && raw !== null && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n)) return n;
    }
  }
  const validation = control.validation;
  if (!Array.isArray(validation)) return undefined;
  for (const rule of validation) {
    const ruleName = String(rule?.rule ?? "").toLowerCase().replace(/_/g, "");
    if (ruleName === "maxvalue" && rule.value !== undefined && rule.value !== null && rule.value !== "") {
      const n = Number(rule.value);
      if (Number.isFinite(n)) return n;
    }
    if (ruleName === "condition" && rule.condition) {
      const bound = parseNumericBoundFromCondition(String(rule.condition), "<=");
      if (bound !== undefined) return bound;
    }
  }
  return undefined;
}

/** True when any conditionalRequired rule on this field is active for the current form/row. */
export function isFieldConditionallyRequired(
  fieldConfig: {
    validation?: Array<{
      rule?: string;
      whenField?: string;
      whenLabel?: string;
      whenValue?: string;
      whenNotEmpty?: boolean;
    }>;
  },
  data: Record<string, any>,
  siblingFields: ComponentConfig[] | undefined,
  lookups?: Record<
    string,
    Array<{ value?: unknown; id?: unknown; label?: string; name?: string }>
  >
): boolean {
  const validation = fieldConfig.validation;
  if (!validation || !Array.isArray(validation)) return false;
  for (const rule of validation) {
    if (isConditionalRequiredRule(rule) && rule.whenField) {
      if (isConditionallyRequired(rule, data, siblingFields, lookups)) return true;
    }
  }
  return false;
}
